import { jsx } from 'react/jsx-runtime';
import { createContext, useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { LOCAL_FEATURE_FLAGS } from './feature-flags-helper.js';
import { getURLParamsFeatureFlagOverrides, getFeatureFlagOverridesFromEnv } from './feature-flag-overrides.js';
import { DEFAULT_CLIENT_FEATURE_FLAGS, FEATURE_FLAG_QUERY_VARIABLES, mapFeatureFlagResponse } from './feature-flags-utils.js';
import { logError } from '../errors/error-reporting.js';
import { gql } from 'urql';
import { useQuery } from '../graphql/graphql.js';
import '../authentication/AuthenticationContextProvider.js';
import { useIsAuthenticated, useIsInternalUser } from '../authentication/permissions.js';
import { GetFeatureFlagsV2Context } from '../generated/graphql.js';

/**
 * Default values for all feature flags, both from the GQL API and local feature flags.
 */ const DEFAULT_FEATURE_FLAGS = {
    ...DEFAULT_CLIENT_FEATURE_FLAGS,
    ...LOCAL_FEATURE_FLAGS
};
const GET_FEATURE_FLAGS = gql`
  query GetFeatureFlagsV2($flags: [FeatureFlagRequest!]!) {
    featureFlagsV2(flags: $flags) {
      key
      value {
        ... on BoolValue {
          boolValue: value
        }
        ... on StringValue {
          stringValue: value
        }
        ... on IntValue {
          intValue: value
        }
      }
    }
  }
`;
/**
 * @internal This should not be used directly, use the `FeatureFlagContextProvider` instead
 */ const FeatureFlagContext = /*#__PURE__*/ createContext({
    isLoading: false,
    originalFeatureFlags: DEFAULT_FEATURE_FLAGS,
    featureFlags: DEFAULT_FEATURE_FLAGS,
    setFeatureFlags: ()=>{
    // noop
    }
});
/**
 * Context provider that loads feature flag state from the GQL API, combines it with any local flag state, and makes
 * the flags available to the rest of the application.
 */ function FeatureFlagContextProvider(props) {
    const isAuthenticated = useIsAuthenticated();
    const history = useHistory();
    const [{ data, error, fetching }] = useQuery({
        query: GET_FEATURE_FLAGS,
        context: GetFeatureFlagsV2Context,
        variables: FEATURE_FLAG_QUERY_VARIABLES,
        pause: !isAuthenticated
    });
    // Since we "handle" this error instead of throwing it, make sure it gets reported
    useEffect(()=>{
        if (error === undefined || error === null) return;
        logError(error);
    }, [
        error
    ]);
    const flags = {
        ...mapFeatureFlagResponse(data),
        ...LOCAL_FEATURE_FLAGS
    };
    const [localClientOverrides, setLocalClientOverrides] = useState({});
    const paramOverrides = getURLParamsFeatureFlagOverrides(history.location.search);
    const isChronoAdmin = useIsInternalUser();
    const envConfigLocalOverrides = getFeatureFlagOverridesFromEnv();
    // Only merge the flags if the user is a Chrono Admin
    const mergedFlags = isChronoAdmin ? {
        ...flags,
        ...envConfigLocalOverrides,
        ...localClientOverrides,
        ...paramOverrides
    } : flags;
    return /*#__PURE__*/ jsx(FeatureFlagContext.Provider, {
        value: {
            isLoading: fetching,
            originalFeatureFlags: flags,
            featureFlags: mergedFlags,
            setFeatureFlags: setLocalClientOverrides
        },
        children: props.children
    });
}
function useFeatureFlagsState() {
    const { featureFlags, setFeatureFlags, originalFeatureFlags } = useContext(FeatureFlagContext);
    return {
        originalFeatureFlags,
        featureFlags,
        setFeatureFlags
    };
}
function useFeatureFlags() {
    const { featureFlags } = useFeatureFlagsState();
    return featureFlags;
}
function useIsFeatureFlagsLoading() {
    const featureFlagsContext = useContext(FeatureFlagContext);
    return featureFlagsContext.isLoading;
}

export { DEFAULT_FEATURE_FLAGS, FeatureFlagContext, FeatureFlagContextProvider, useFeatureFlags, useFeatureFlagsState, useIsFeatureFlagsLoading };
//# sourceMappingURL=FeatureFlags.js.map
